import {
  type SearchWorkplacesRequest,
  type SearchWorkplacesResponse,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api";
import { get } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { minutesToMilliseconds } from "date-fns";
import { z } from "zod";

const workplaceSearchResultApiTypeAndId = z.object({
  id: z.string(),
  type: z.literal(API_TYPES.workplaceSearchResult),
});

const workplaceSearchResult = z.object({
  ...workplaceSearchResultApiTypeAndId.shape,
  attributes: z
    .object({
      name: z.string(),
      openShiftCount: z.number(),
      type: z.string().optional(),
      msa: z.string().optional(),
      state: z.string().optional(),
      region: z.string().optional(),
      distanceFromWorker: z.number(),
    })
    .partial(),
});

// We should expose this in the contract-worker-app-bff package
export const searchWorkplacesResponse = z.object({
  data: z.array(workplaceSearchResult),
  count: z.number(),
});

export const SEARCH_WORKPLACES_URL = `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}/workplaces`;

export function useOtherWorkplaces(
  queryParams: SearchWorkplacesRequest,
  options: Omit<
    UseInfiniteQueryOptions<SearchWorkplacesResponse, Error>,
    "queryKey" | "queryFn" | "getNextPageParam"
  > & {
    /**
     * Optional limit on how many pages the client can load,
     * regardless of how many total items are available from the server.
     */
    maxPagesToLoad?: number;
  } = {}
) {
  const pageSize = queryParams.page?.size ?? 20;
  const { maxPagesToLoad, ...queryOptions } = options;

  return useInfiniteQuery<SearchWorkplacesResponse, Error>({
    queryKey: [SEARCH_WORKPLACES_URL, queryParams.filter],
    queryFn: async ({ pageParam: pageParameter = 0 }: QueryFunctionContext<QueryKey, number>) => {
      const response = await get({
        url: SEARCH_WORKPLACES_URL,
        queryParams: {
          ...queryParams,
          page: {
            size: pageSize,
            offset: pageParameter,
          },
        },
        responseSchema: searchWorkplacesResponse,
      });

      return response.data;
    },
    getNextPageParam: (
      lastPage: SearchWorkplacesResponse,
      allPages: SearchWorkplacesResponse[]
    ) => {
      if (isDefined(maxPagesToLoad) && allPages.length >= maxPagesToLoad) {
        return undefined;
      }

      const totalItems = lastPage.count;
      const fetchedItemsCount = pageSize * allPages.length;

      if (fetchedItemsCount >= totalItems) {
        return undefined; // No more pages to fetch
      }

      return fetchedItemsCount;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKPLACES_FAILURE,
      userErrorMessage: "Failed to fetch workplaces",
    },
    staleTime: minutesToMilliseconds(5),
    ...queryOptions,
  });
}
